import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { startAuthentication, type PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser'
import { KeyRound } from 'lucide-react'
import { Button as SButton } from '@/components/shadcn/button'
import { Button, Input } from '@/components/ui'
import { ApiError, post } from '@/lib/api'
import { qk } from '@/lib/query'
import { AuthCard, FormError } from './AuthCard'
import { OtpInput } from './OtpInput'
import { useMe } from './useMe'
import { challengeFor } from '@/lib/can'

/**
 * The 2FA challenge (03 §5.2). Asks for whichever factor is enrolled; if both,
 * TOTP (decision 15b). `challengeFor` derives it from the enrolled factors
 * `/auth/me` reports, the same rule the Worker's login answer follows.
 */
export function TwoFactorPage() {
  const me = useMe().data
  const challenge = me ? challengeFor(me) : null
  const nav = useNavigate()
  const qc = useQueryClient()
  const search = useSearch({ strict: false }) as { next?: string }
  const [mode, setMode] = useState<'factor' | 'recovery'>('factor')
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function done() {
    await qc.invalidateQueries({ queryKey: qk.me() })
    await nav({ to: search.next && search.next.startsWith('/') ? search.next : '/' })
  }

  async function run(fn: () => Promise<unknown>, failure: string) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await done()
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? 'Too many attempts. Wait a few minutes.' : failure)
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  const verifyTotp = (c: string) => run(() => post('/auth/2fa/totp/verify', { code: c }), "That code didn't work. Codes change every 30 seconds — try the current one.")

  const verifyPasskey = () =>
    run(async () => {
      const { challengeId, options } = await post<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }>('/auth/2fa/passkey/options', {})
      const response = await startAuthentication({ optionsJSON: options })
      await post('/auth/2fa/passkey/verify', { challengeId, response })
    }, 'The passkey check did not complete. Try again, or use a recovery code.')

  const useRecovery = () => run(() => post('/auth/2fa/recovery', { code: recovery.trim() }), 'That recovery code is not valid or was already used.')

  if (me && me.stage === 'password' && !challenge) {
    // Password accepted but no factor yet: enrolment is compulsory.
    void nav({ to: '/enrol' })
  }

  return (
    <AuthCard title="Two-factor check">
      <FormError>{error}</FormError>
      {mode === 'recovery' ? (
        <div>
          <p className="mb-3 text-xs text-fg-subtle">Enter one of the recovery codes you saved when you set up two-factor. Each works once.</p>
          <Input value={recovery} onChange={(e) => setRecovery(e.target.value)} placeholder="xxxx-xxxx-xxxx" className="font-mono" autoFocus />
          <Button variant="primary" size="md" className="mt-3 w-full" disabled={busy || recovery.length < 8} onClick={useRecovery}>
            Use recovery code
          </Button>
        </div>
      ) : challenge === 'passkey' ? (
        <div>
          <p className="mb-4 text-xs text-fg-subtle">Use your passkey.</p>
          <Button variant="primary" size="md" className="w-full" disabled={busy} onClick={verifyPasskey}>
            <KeyRound size={14} /> Continue with passkey
          </Button>
        </div>
      ) : (
        <div>
          <p className="mb-4 text-xs text-fg-subtle">Enter the 6-digit code from your authenticator app.</p>
          <OtpInput value={code} onChange={setCode} onComplete={verifyTotp} disabled={busy} />
        </div>
      )}
      <SButton variant="link" className="mt-4 h-auto p-0 text-xs font-normal text-fg-subtle hover:text-fg" onClick={() => setMode(mode === 'recovery' ? 'factor' : 'recovery')}>
        {mode === 'recovery' ? 'Back' : 'Use a recovery code instead'}
      </SButton>
    </AuthCard>
  )
}
